import { createSelector } from '@reduxjs/toolkit';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getId, getEmail } from '../state/user';

interface ContextType {
  appcues: Window['Appcues'] | null;
  loading: boolean;
  error: string | null;
}

const Context = createContext<ContextType>({ appcues: null, loading: false, error: null });

interface ProviderProps {
  children: React.ReactNode;
}

const selector = createSelector(getId, getEmail, (userId, email) => ({
  userId,
  email,
}));

export const Provider = ({ children }: ProviderProps) => {
  const [appcues, setAppcues] = useState<Window['Appcues'] | null>(null);
  const { email, userId } = useSelector(selector);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    // stop looking for Appcues if it cannot find it for a while
    const timeout = setTimeout(() => {
      if (!('Appcues' in window)) {
        setLoading(false);
        setError('Timedout loading Appcues');
      }
    }, 10000);

    // wait for Appcues to load
    const interval = setInterval(() => {
      if ('Appcues' in window) {
        clearInterval(interval);
        clearTimeout(timeout);
        setAppcues(window.Appcues);
        setLoading(false);
      }
    }, 300);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // track user
  useEffect(() => {
    if (!appcues) {
      return;
    }

    if (userId) {
      appcues.identify(Number(userId), { email });
    } else {
      appcues.reset();
    }
  }, [appcues, email, userId]);

  const value = useMemo(() => ({ appcues, loading, error }), [appcues, loading, error]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
